import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

//Bootstrap
import { Card, Form } from "react-bootstrap";

//API calls
import {
  useGetSifrantVlogaPartnerja,
  useGetSifrantPartnerjev,
  useAddOtherPartnerToProject,
} from "../../../../services/ProjectDetails/partnerskeOrganizacijeAPI";

//UI components
import FormSelect from "../../../ui/FormSelect";
import FormSelectSearchAsyncField from "../../../ui/FormSelectSearchAsyncField";
import FormField from "../../../ui/FormField";
import ErrorMessage from "../../../ui/ErrorMessage";

//Utils
import { useDebounceHook } from "../../../utils/useDebounceHook";
import sifrantSetSelectValueAndLabel from "../../../utils/sifrantSetSelectValueAndLabel";

//Validation schema
import formAddOtherPartnerToProjectSchema from "../../../../config/formAddOtherPartnerToProject";

const AddPartnerToProjectForm = ({ setIsAdding }) => {
  const { id } = useParams();

  const [searchTermProjektniPartner, setSearchTermProjektniPartner] =
    useState("");

  //useMemo for schema to prevent unnecessary re-renders of the form
  const schema = useMemo(() => formAddOtherPartnerToProjectSchema, []);

  //GET Vloge za partnerja in
  const { data: sifrantVloga } = useGetSifrantVlogaPartnerja(true);
  //GET sifrant partnerjev
  const { data: sifrantPartnerjev, isLoading: isLoadingSifrantPartnerjev } =
    useGetSifrantPartnerjev(useDebounceHook(searchTermProjektniPartner, 300));

  //POST add other partner to project
  const {
    mutate: addOtherPartnerToProject,
    isPending: isPendingAddOtherPartner,
    isError: isError,
    error,
    reset: resetMutationError,
  } = useAddOtherPartnerToProject(id);

  //INIT form with react-hook-form and zod validation
  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    register,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      partnerSelected: null,
      vlogaID: null,
      vodjaProjekta: "",
      kontaktnaOseba: "",
    },
  });

  //setSifrantVlogaOptions in format for select component
  const sifrantVlogaOptions = sifrantSetSelectValueAndLabel(sifrantVloga);

  //setSifrantPartnerjevOptions in format for select component
  const sifrantPartnerjevOptions =
    sifrantSetSelectValueAndLabel(sifrantPartnerjev);

  //onSubmit function to call mutation for adding other partner to project
  const onSubmit = (data) => {
    const payload = {
      partnerID: data.partnerSelected.value,
      vlogaID: data.vlogaID,
      vodjaProjekta: data.vodjaProjekta,
      kontaktnaOseba: data.kontaktnaOseba,
    };

    addOtherPartnerToProject(payload, {
      onSuccess: () => {
        setIsAdding(false);
      },
      onError: () => setTimeout(() => resetMutationError(), 3000),
    });
  };

  return (
    <Card className="mb-4 p-3 mt-4">
      <h4 className="form-section-heading">Dodaj partnerja na projekt</h4>
      <Form className="mt-2" onSubmit={handleSubmit((data) => onSubmit(data))}>
        <FormSelectSearchAsyncField
          label="Izberi partnerja"
          name="partnerSelected"
          control={control}
          options={sifrantPartnerjevOptions}
          setSearchTerm={setSearchTermProjektniPartner}
          placeholder="Izberi partnerja"
          isLoading={isLoadingSifrantPartnerjev}
          validationError={errors.partnerSelected?.message}
        />
        <FormSelect
          label="Vloga partnerja"
          name="vlogaID"
          control={control}
          options={sifrantVlogaOptions}
          validationError={errors.vlogaID?.message}
        />
        <FormField
          label="Vodja projekta"
          formControlType="text"
          register={register("vodjaProjekta")}
        />
        <FormField
          label="Kontaktna oseba"
          formControlType="text"
          register={register("kontaktnaOseba")}
        />
        <button
          type="submit"
          className="btn btn-primary mt-3"
          disabled={!isDirty || isPendingAddOtherPartner}
        >
          {isPendingAddOtherPartner ? "Shranjevanje..." : "Shrani"}
        </button>
        <button
          type="button"
          className="btn btn-secondary mt-3 ms-2"
          onClick={() => setIsAdding(false)}
        >
          Prekliči
        </button>
        {isError && <ErrorMessage message={error.message} />}
      </Form>
    </Card>
  );
};

export default AddPartnerToProjectForm;
